import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Shield, ShieldAlert } from 'lucide-react';
import {
  getLscArchiveAutopublishDryRunPreview,
  getLscArchiveAutopublishEligibility,
  getLscArchiveAutopublishSafety,
  getLscArchiveAutopublishSettings,
  LscApiError,
  updateLscArchiveAutopublishSettings,
} from '../../api';
import { archiveAutopublishBlockerCustomerLabels } from '../../archiveAutopublishBlockerCopy';
import {
  ARCHIVE_RETENTION_SETTINGS_COPY,
  ARCHIVE_RETENTION_SUMMARY_COPY,
} from '../../archiveRetentionCopy';
import {
  ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY,
  ARCHIVE_AUTOPUBLISH_MANUAL_REVIEW_FALLBACK_COPY,
  ARCHIVE_AUTOPUBLISH_NO_PUBLISH_WILL_HAPPEN_COPY,
  archiveAutopublishDryRunPreviewCustomerCopy,
  archiveAutopublishKillSwitchCustomerCopy,
  archiveAutopublishSettingsCustomerCopy,
} from '../../commandCenterCopy';
import { parseLscMutationError } from '../../entitlementBillingErrors';
import { lscPath } from '../../routing';
import type {
  LscArchiveAutopublishDryRunPreview,
  LscArchiveAutopublishEligibility,
  LscArchiveAutopublishKillSwitch,
  LscArchiveAutopublishSettings,
} from '../../types';
import ErrorBanner from '../ErrorBanner';
import LoadingState from '../LoadingState';
import LscMutationErrorAlert from '../LscMutationErrorAlert';

const FIELD_CLASS =
  'w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-secondary-900 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

export const ARCHIVE_AUTOPUBLISH_ASK_OWNER_COPY =
  'Ask the league owner to change archive autopublish settings.';

export const ARCHIVE_AUTOPUBLISH_STAGE2_INTRO_COPY =
  'Dry run shows what would happen without publishing. Real archive auto-publish stays off until a later controlled apply gate.';

export const ARCHIVE_AUTOPUBLISH_SAFETY_REQUIREMENTS_COPY =
  'These safety requirements stay active and cannot be disabled from this panel.';

export type ArchiveAutopublishSettingsPanelProps = {
  leagueId: string;
  canMutate: boolean;
  canRead?: boolean;
  leagueArchived?: boolean;
  candidateId?: string;
};

type SafeMode = 'off' | 'dry_run';

const SAFETY_REQUIREMENT_ITEMS = [
  { key: 'require_verified_live_handoff', label: 'Verified live handoff required' },
  { key: 'require_confirmed_broadcaster_side', label: 'Broadcaster side confirmation required' },
  { key: 'require_no_needs_attention', label: 'Needs Attention must be clear' },
  {
    key: 'require_no_schedule_stale_or_rollover_blocker',
    label: 'Schedule stale and rollover blockers must be clear',
  },
  { key: 'require_no_team_or_opponent_conflict', label: 'Team and opponent conflicts must be clear' },
  { key: 'manual_review_fallback_enabled', label: 'Manual review fallback remains enabled' },
  { key: 'manual_hold_blocks_autopublish', label: 'Manual hold blocks autopublish' },
] as const;

function normalizeDraftMode(settings: LscArchiveAutopublishSettings | null): SafeMode {
  const mode = (settings?.autopublish_mode || 'off').trim().toLowerCase();
  if (mode === 'dry_run' && settings?.autopublish_enabled) return 'dry_run';
  return 'off';
}

const ArchiveAutopublishSettingsPanel: React.FC<ArchiveAutopublishSettingsPanelProps> = ({
  leagueId,
  canMutate,
  canRead = true,
  leagueArchived = false,
  candidateId,
}) => {
  const [settings, setSettings] = useState<LscArchiveAutopublishSettings | null>(null);
  const [eligibility, setEligibility] = useState<LscArchiveAutopublishEligibility | null>(null);
  const [preview, setPreview] = useState<LscArchiveAutopublishDryRunPreview | null>(null);
  const [safety, setSafety] = useState<LscArchiveAutopublishKillSwitch | null>(null);
  const [draftMode, setDraftMode] = useState<SafeMode>('off');
  const [changeReason, setChangeReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<ReturnType<typeof parseLscMutationError> | null>(
    null
  );
  const [success, setSuccess] = useState<string | null>(null);

  const loadPanel = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const options = candidateId ? { candidateId } : undefined;
      const [settingsPayload, eligibilityPayload, previewPayload, safetyPayload] = await Promise.all([
        getLscArchiveAutopublishSettings(leagueId),
        getLscArchiveAutopublishEligibility(leagueId, options),
        getLscArchiveAutopublishDryRunPreview(leagueId, options),
        getLscArchiveAutopublishSafety(leagueId, options),
      ]);
      setSettings(settingsPayload);
      setEligibility(eligibilityPayload);
      setPreview(previewPayload);
      setSafety(safetyPayload);
      setDraftMode(normalizeDraftMode(settingsPayload));
    } catch (err) {
      setLoadError(
        err instanceof LscApiError
          ? err.message
          : 'Unable to load archive autopublish settings right now.'
      );
    } finally {
      setLoading(false);
    }
  }, [candidateId, leagueId]);

  useEffect(() => {
    if (!canRead) return;
    void loadPanel();
  }, [canRead, loadPanel]);

  const settingsMode = settings?.autopublish_mode || 'off';
  const controlledApplyLocked = settingsMode === 'controlled_apply';
  const modeCopy = archiveAutopublishSettingsCustomerCopy(settingsMode);
  const previewCopy = archiveAutopublishDryRunPreviewCustomerCopy(preview?.preview_mode);
  const safetyCopy = archiveAutopublishKillSwitchCustomerCopy(safety);

  const blockerLabels = useMemo(
    () =>
      archiveAutopublishBlockerCustomerLabels([
        ...(eligibility?.blocking_reasons || []),
        ...(preview?.blocking_reasons || []),
        ...(safety?.disabled_reason_codes || []),
      ]),
    [eligibility?.blocking_reasons, preview?.blocking_reasons, safety?.disabled_reason_codes]
  );

  const handleSave = async () => {
    if (!canMutate || leagueArchived) return;
    setSaving(true);
    setMutationError(null);
    setSuccess(null);
    try {
      const updated = await updateLscArchiveAutopublishSettings(leagueId, {
        archive_autopublish_settings: {
          autopublish_mode: draftMode,
          autopublish_enabled: draftMode !== 'off',
        },
        change_reason: changeReason.trim() || undefined,
      });
      setSettings(updated);
      setDraftMode(normalizeDraftMode(updated));
      setSuccess('Archive autopublish settings saved.');
      await loadPanel();
    } catch (err) {
      setMutationError(parseLscMutationError(err, 'Unable to save archive autopublish settings.'));
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return (
      <div className="card text-sm text-secondary-300" data-testid="archive-autopublish-read-denied">
        {ARCHIVE_AUTOPUBLISH_ASK_OWNER_COPY}
      </div>
    );
  }

  if (loading) {
    return <LoadingState message="Loading archive autopublish settings..." />;
  }

  if (loadError) {
    return (
      <div className="space-y-3" data-testid="archive-autopublish-settings-panel">
        <ErrorBanner message={loadError} />
        <button type="button" className="btn-secondary text-sm" onClick={() => void loadPanel()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5 min-w-0 overflow-x-hidden" data-testid="archive-autopublish-settings-panel">
      <div
        className="rounded-lg border border-secondary-700/80 bg-secondary-900/30 px-4 py-3 text-sm text-secondary-300"
        data-testid="archive-retention-summary"
      >
        <p>{ARCHIVE_RETENTION_SUMMARY_COPY}</p>
        <p className="mt-2 text-secondary-400">{ARCHIVE_RETENTION_SETTINGS_COPY}</p>
      </div>

      <div className="card space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-white">Archive auto-publish (Stage 2)</h3>
          <span
            className="inline-flex items-center rounded-full border border-secondary-600 bg-secondary-800/60 px-2.5 py-0.5 text-xs font-semibold text-secondary-200"
            data-testid="archive-autopublish-stage2-badge"
          >
            No automatic publishing yet
          </span>
        </div>
        <p className="text-sm text-secondary-400">{ARCHIVE_AUTOPUBLISH_STAGE2_INTRO_COPY}</p>
        <p className="text-sm text-secondary-200" data-testid="archive-autopublish-current-mode-copy">
          {modeCopy}
        </p>
        <p className="text-sm text-secondary-300">{ARCHIVE_AUTOPUBLISH_NO_PUBLISH_WILL_HAPPEN_COPY}</p>
      </div>

      <div className="card space-y-4 min-w-0">
        <h4 className="font-medium text-white">Safe mode</h4>
        <fieldset className="space-y-3 text-sm" disabled={!canMutate || leagueArchived || saving}>
          <label className="flex items-start gap-3 min-h-[44px] py-1">
            <input
              type="radio"
              name="archive-autopublish-mode"
              value="off"
              checked={draftMode === 'off'}
              onChange={() => setDraftMode('off')}
              data-testid="archive-autopublish-mode-off"
            />
            <span>
              <span className="font-medium text-white">Off</span>
              <span className="block text-secondary-400">
                Archive auto-publish is off. Commissioner review remains required before publish.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 min-h-[44px] py-1">
            <input
              type="radio"
              name="archive-autopublish-mode"
              value="dry_run"
              checked={draftMode === 'dry_run'}
              onChange={() => setDraftMode('dry_run')}
              data-testid="archive-autopublish-mode-dry-run"
            />
            <span>
              <span className="font-medium text-white">Dry run</span>
              <span className="block text-secondary-400">
                Dry run shows what would happen without publishing.
              </span>
            </span>
          </label>
          <label
            className="flex items-start gap-3 min-h-[44px] py-1 opacity-70"
            data-testid="archive-autopublish-mode-controlled-locked"
          >
            <input type="radio" name="archive-autopublish-mode" value="controlled_apply" disabled />
            <span>
              <span className="font-medium text-secondary-300">Controlled apply (locked)</span>
              <span className="block text-secondary-500">
                {ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY}
              </span>
            </span>
          </label>
        </fieldset>

        {!canMutate ? (
          <p className="text-sm text-secondary-400" data-testid="archive-autopublish-ask-owner">
            {ARCHIVE_AUTOPUBLISH_ASK_OWNER_COPY}
          </p>
        ) : null}

        {canMutate ? (
          <>
            <label className="block space-y-1 text-sm">
              <span className="text-secondary-300">Change reason (optional)</span>
              <input
                className={FIELD_CLASS}
                value={changeReason}
                disabled={leagueArchived || saving}
                onChange={(event) => setChangeReason(event.target.value)}
                placeholder="Why are you changing archive autopublish settings?"
              />
            </label>
            <LscMutationErrorAlert error={mutationError} onDismiss={() => setMutationError(null)} />
            {success ? <p className="text-sm text-emerald-400">{success}</p> : null}
            <button
              type="button"
              className="btn-primary"
              disabled={leagueArchived || saving}
              onClick={() => void handleSave()}
              data-testid="archive-autopublish-save"
            >
              {saving ? 'Saving…' : 'Save archive autopublish settings'}
            </button>
          </>
        ) : null}
      </div>

      <div className="card space-y-3 min-w-0" data-testid="archive-autopublish-safety-requirements">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary-300" aria-hidden />
          <h4 className="font-medium text-white">Safety requirements</h4>
        </div>
        <p className="text-sm text-secondary-400">{ARCHIVE_AUTOPUBLISH_SAFETY_REQUIREMENTS_COPY}</p>
        <ul className="space-y-2 text-sm">
          {SAFETY_REQUIREMENT_ITEMS.map(({ key, label }) => {
            const active = settings?.[key as keyof LscArchiveAutopublishSettings];
            const checked = active !== false;
            return (
              <li key={key} className="flex items-center gap-2 text-secondary-200">
                <input type="checkbox" checked={checked} disabled readOnly aria-label={label} />
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="card space-y-3 min-w-0" data-testid="archive-autopublish-safety-status">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-300" aria-hidden />
          <h4 className="font-medium text-white">Safety status</h4>
        </div>
        <p className="text-sm text-secondary-200">{safetyCopy}</p>
        {safety?.kill_switch_active ? (
          <p className="text-sm text-amber-200">Safety controls are active.</p>
        ) : (
          <p className="text-sm text-secondary-400">
            Safety controls are active. No publish behavior is enabled.
          </p>
        )}
        {controlledApplyLocked ? (
          <p className="text-sm text-secondary-300">{ARCHIVE_AUTOPUBLISH_CONTROLLED_LOCKED_COPY}</p>
        ) : null}
      </div>

      <div className="card space-y-3 min-w-0" data-testid="archive-autopublish-dry-run-preview">
        <h4 className="font-medium text-white">Dry-run preview</h4>
        <p className="text-sm text-secondary-200">{previewCopy}</p>
        {preview?.would_publish_if_controlled_apply_enabled ? (
          <p className="text-sm text-secondary-300">
            This archive would be ready in dry run. No archive will be published yet.
          </p>
        ) : null}
        {preview?.would_route_to_review ? (
          <p className="text-sm text-secondary-300">This archive would still need review.</p>
        ) : null}
        <p className="text-sm text-secondary-400">{ARCHIVE_AUTOPUBLISH_MANUAL_REVIEW_FALLBACK_COPY}</p>
        {preview?.candidate_summary?.matchup_label ? (
          <p className="text-xs text-secondary-500">
            Preview context: {preview.candidate_summary.matchup_label}
          </p>
        ) : null}
      </div>

      {blockerLabels.length > 0 ? (
        <div className="card space-y-3 min-w-0" data-testid="archive-autopublish-blockers">
          <h4 className="font-medium text-white">Eligibility and blockers</h4>
          <ul className="list-disc space-y-1 pl-5 text-sm text-secondary-200">
            {blockerLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-xs text-secondary-500">
        Need billing help?{' '}
        <Link to={lscPath('/billing')} className="text-primary-300 hover:text-primary-200">
          View billing
        </Link>
      </p>
    </div>
  );
};

export default ArchiveAutopublishSettingsPanel;
